// Read-only detail view for a KPI. Shows definition, target, frequency,
// SLA metadata, and (if present) the isSla/serviceCreditFormula fields.
// Mirrors the InsuranceDetailExpand pattern; used inline in the KPI
// tab's definitions view + inside KpiDetailModal for archive deep-link.
//
// The clause-reference row reads
// `item.kpiClauseReference ?? item.slaClauseReference` so KPIs created
// with only the new field still render the row.

import { Pencil } from "lucide-react";
import Button from "../../../common/Button";
import OpenIssueCountBadge from "../shared/OpenIssueCountBadge";
import type { Kpi } from "../../../../types";

interface KpiDetailExpandProps {
  item: Kpi;
  onEdit?: () => void;
}

function DL({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-caption text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-body text-slate-900 dark:text-slate-100">
        {children}
      </dd>
    </div>
  );
}

export default function KpiDetailExpand({ item, onEdit }: KpiDetailExpandProps) {
  // Fall back to the legacy field name for records written
  // before the schema migration, so both paths surface the reference.
  const clauseRef = item.kpiClauseReference ?? item.slaClauseReference;
  return (
    <section
      data-testid="kpi-detail-expand"
      className="space-y-5"
      aria-label={`Details for ${item.name}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">
            #{item.kpiNumber} — {item.name}
          </h3>
          <OpenIssueCountBadge
            contractId={item.contractId}
            targetType="kpi"
            targetId={item.id}
          />
        </div>
        {onEdit && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            onClick={onEdit}
          >
            Edit
          </Button>
        )}
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
        <DL label="Target">{item.target}</DL>
        <DL label="Measurement frequency">{item.measurementFrequency}</DL>
        <DL label="Reporting frequency">{item.reportingFrequency}</DL>
        {typeof item.reportDueOffset === "number" && (
          <DL label="Report due offset">{item.reportDueOffset} day(s)</DL>
        )}
        <DL label="SLA">{item.isSla ? "Yes" : "No"}</DL>
        {item.isSla && clauseRef && (
          <DL label="SLA clause">{clauseRef}</DL>
        )}
        {item.isSla && item.serviceCreditFormula && (
          <DL label="Service credit formula">{item.serviceCreditFormula}</DL>
        )}
        {typeof item.higherIsBetter === "boolean" && (
          <DL label="Direction">
            {item.higherIsBetter ? "Higher is better" : "Lower is better"}
          </DL>
        )}
      </dl>

      <div>
        <h4 className="text-caption text-slate-500 dark:text-slate-400">
          Definition
        </h4>
        <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
          {item.definition}
        </p>
      </div>

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </section>
  );
}
